<template>
  <div class="auth-callback-view">
    <div class="loading-container">
      <div class="loading-spinner" />
      <p class="loading-text">
        {{ $t('auth.authenticating') }}
      </p>
    </div>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/store/auth';
import { usePageSeo } from '@/composables/usePageSeo';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();

usePageSeo({
  noindex: true,
});

onMounted(async () => {
  try {
    // Get the authorization code from URL parameters
    const code = route.query.code;
    const state = route.query.state;

    // Validate state to prevent CSRF attacks
    if (state !== 'apple-sign-in') {
      throw new Error('Invalid state parameter');
    }

    if (!code) {
      throw new Error('No authorization code received');
    }

    // Handle the Apple sign-in callback
    const result = await authStore.handleAppleCallback({
      code,
      state,
    });

    if (result.success) {
      // Redirect to dashboard or intended destination
      router.push('/dashboard');
    } else {
      // Handle error
      router.push({
        name: 'login',
        query: { error: 'auth_failed' },
      });
    }
  } catch (error) {
    console.error('Authentication callback error:', error);
    // Redirect to login with error
    router.push({
      name: 'login',
      query: { error: 'auth_failed' },
    });
  }
});
</script>

<style lang="scss" scoped>
.auth-callback-view {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background-color: var(--color-background);
}

.loading-container {
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto 1rem;
  border: 3px solid rgba(var(--color-primary-rgb), 0.1);
  border-radius: 50%;
  border-top-color: var(--color-primary);
  animation: spin 1s linear infinite;
}

.loading-text {
  color: var(--color-text);
  font-size: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>